import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { DECIMAL_TEXT_INPUT_ATTRIBUTES, DecimalErrorKind, DecimalFieldPolicy, decimalErrorMessageKind, isCommittable, parseDecimalInput } from '../utils/decimalInput';

export type DecimalEmptyPolicy = 'publishEmpty' | 'publishZero' | 'publishNull' | 'restoreStore';

export type DecimalPublishEvent =
    | { kind: 'value'; value: number; text: string }
    | { kind: 'empty'; value: '' | 0 | null }
    | { kind: 'pending'; raw: string }
    | { kind: 'error'; raw: string; errorKind: DecimalErrorKind };

export interface UseDecimalDraftOptions {
    value: unknown;
    policy: DecimalFieldPolicy;
    emptyPolicy: DecimalEmptyPolicy;
    formatBlurred?: (value: unknown) => string;
    onPublish: (event: DecimalPublishEvent) => void;
    onValidityChange?: (isValid: boolean) => void;
    /** A change resets the draft to the stored value, unless the field is being edited. */
    resetKey?: string | number;
}

export interface DecimalDraft {
    inputProps: {
        type: 'text';
        inputMode: 'decimal';
        value: string;
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
        onFocus: () => void;
        onBlur: () => void;
        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    };
    isValid: boolean;
    errorKind: DecimalErrorKind | null;
    reset: () => void;
}

const EMPTY_VALUES: Record<Exclude<DecimalEmptyPolicy, 'restoreStore'>, '' | 0 | null> = { publishEmpty: '', publishZero: 0, publishNull: null };

const storedText = (value: unknown): string => String(value ?? '');

export const useDecimalDraft = ({ value, policy, emptyPolicy, formatBlurred = storedText, onPublish, onValidityChange, resetKey }: UseDecimalDraftOptions): DecimalDraft => {
    const [rawText, setRawText] = useState<string>(() => storedText(value));
    const [uncommitted, setUncommitted] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [errorRevealed, setErrorRevealed] = useState(false);
    const [seenResetKey, setSeenResetKey] = useState(resetKey);

    const applyValidity = (isValid: boolean) => {
        setUncommitted(!isValid);
        if (isValid === uncommitted) onValidityChange?.(isValid);
    };

    if (resetKey !== seenResetKey) {
        setSeenResetKey(resetKey);
        if (!isFocused) {
            setRawText(storedText(value));
            setUncommitted(false);
            setErrorRevealed(false);
        }
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value;
        setRawText(text);
        setErrorRevealed(false);
        if (text.trim() === '') {
            applyValidity(true);
            if (emptyPolicy !== 'restoreStore') onPublish({ kind: 'empty', value: EMPTY_VALUES[emptyPolicy] });
            // The text will come back to the stored value: publish it too, so a raw text
            // published before (and still held by Formik) does not stay behind, hidden
            else if (isCommittable(value, policy)) onPublish({ kind: 'value', value: Number(value), text: storedText(value) });
            return;
        }
        const parsed = parseDecimalInput(text, policy);
        applyValidity(isCommittable(text, policy));
        onPublish(parsed.kind === 'value' ? { kind: 'value', value: parsed.value, text: parsed.normalized } : { kind: 'pending', raw: text });
    };

    const commit = () => {
        if (!uncommitted) {
            if (rawText.trim() === '' && emptyPolicy === 'restoreStore') setRawText(storedText(value));
            return;
        }
        const parsed = parseDecimalInput(rawText, policy);
        if (parsed.kind === 'draft' && parsed.committable !== null) {
            const committedText = String(parsed.committable);
            setRawText(committedText);
            applyValidity(true);
            onPublish({ kind: 'value', value: parsed.committable, text: committedText });
            return;
        }
        setErrorRevealed(true);
        onPublish({ kind: 'error', raw: rawText, errorKind: decimalErrorMessageKind(rawText) ?? 'notANumber' });
    };

    const reset = () => {
        setRawText(storedText(value));
        setErrorRevealed(false);
        applyValidity(true);
    };

    const isErrorVisible = uncommitted && (errorRevealed || !isFocused);

    return {
        inputProps: {
            ...DECIMAL_TEXT_INPUT_ATTRIBUTES,
            value: isFocused || uncommitted ? rawText : formatBlurred(value),
            onChange: handleChange,
            onFocus: () => {
                setIsFocused(true);
                if (!uncommitted) setRawText(storedText(value));
            },
            onBlur: () => {
                setIsFocused(false);
                commit();
            },
            onKeyDown: event => {
                // Formik validates a submit against the values of its last render: render the commit
                // before the browser's implicit form submission that follows this Enter
                if (event.key === 'Enter') flushSync(commit);
            },
        },
        isValid: !uncommitted,
        errorKind: isErrorVisible ? (decimalErrorMessageKind(rawText) ?? 'notANumber') : null,
        reset,
    };
};
